import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  XLogo,
  InstagramLogo,
  LinkedinLogo,
} from "@phosphor-icons/react";

const LOGO_MARK = "/logomark.png";

const SOCIAL_LINKS = [
  { key: "x", href: "https://x.com/adonis_agency_", label: "X", Icon: XLogo },
  {
    key: "instagram",
    href: "https://www.instagram.com/adonis_agency_of",
    label: "Instagram",
    Icon: InstagramLogo,
  },
  {
    key: "linkedin",
    href: "https://linkedin.com/company/adonis-agency",
    label: "LinkedIn",
    Icon: LinkedinLogo,
  },
];

const LEGAL_LINKS = [
  { key: "legal", to: "/legal" },
  { key: "privacy", to: "/privacy" },
  { key: "terms", to: "/terms" },
  { key: "sales_terms", to: "/sales-terms" },
];

const ColumnTitle = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
    {children}
  </p>
);

export const Footer = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="main-footer"
      className="bg-slate-950 text-slate-300 border-t border-slate-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          {/* Brand + tagline */}
          <div className="md:col-span-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_MARK} alt="Adonis Agency" className="h-10 w-10" />
              <span className="font-heading text-xl font-bold tracking-tight text-white">
                Adonis<span className="text-slate-500"> Agency</span>
              </span>
            </div>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Social column */}
          <nav
            data-testid="footer-social"
            aria-label={t("footer.col_social")}
            className="md:col-span-3"
          >
            <ColumnTitle>{t("footer.col_social")}</ColumnTitle>
            <ul className="mt-5 space-y-3">
              {SOCIAL_LINKS.map(({ key, href, label, Icon }) => (
                <li key={key}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${key}`}
                    className="group inline-flex items-center gap-3 text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-800 bg-slate-900/60 group-hover:border-white group-hover:bg-white group-hover:text-slate-900 transition-colors">
                      <Icon size={14} weight="regular" />
                    </span>
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Legal column */}
          <nav
            data-testid="footer-legal"
            aria-label={t("footer.col_legal")}
            className="md:col-span-3"
          >
            <ColumnTitle>{t("footer.col_legal")}</ColumnTitle>
            <ul className="mt-5 space-y-3">
              {LEGAL_LINKS.map(({ key, to }) => (
                <li key={key}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${key}`}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {t(`footer.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-14 pt-6 border-t border-slate-800 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-slate-500">
          <p data-testid="footer-copyright">
            © {year} Adonis Agency. {t("footer.rights")}.
          </p>
          <p data-testid="footer-credit">
            {t("footer.credit_prefix")}{" "}
            <a
              href="https://axeris-labs.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-300 hover:text-white underline underline-offset-4 decoration-slate-600 hover:decoration-white transition-colors"
            >
              Axeris Labs
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
